import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  ArrowLeft,
  CheckCircle2,
  ThumbsUp,
  ThumbsDown,
  Download,
  PieChart,
  Loader2,
  LayoutList,
  LayoutGrid,
  MapPin,
} from "lucide-react";
import documentCompareService from "../../services/document-compare.service";
import Loader from "../../widgets/Loader";
import ComparisonChartModal from "../../components/documents/ComparisonChartModal";

const DocumentCompareResult: React.FC = () => {
  const { compareId } = useParams<{ compareId: string }>();
  const navigate = useNavigate();
  const [compareData, setCompareData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filterType, setFilterType] = useState<string>("all");
  const [filterDisliked, setFilterDisliked] = useState<string>("all"); // all, liked, disliked
  const [showChartModal, setShowChartModal] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<"table" | "cards">("table");
  const [downloadLoading, setDownloadLoading] = useState(false);

  useEffect(() => {
    if (compareId) {
      fetchCompareDetails();
    }
  }, [compareId]);

  const fetchCompareDetails = async () => {
    if (!compareId) return;

    setIsLoading(true);
    try {
      const response = await documentCompareService.getDocumentCompareById(
        compareId
      );
      if (response.success) {
        setCompareData(response.data);
      } else {
        toast.error("Failed to fetch comparison details");
      }
    } catch (error: any) {
      console.error("Error fetching comparison details:", error);
      toast.error("Failed to fetch comparison details");
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleDislike = async (differenceId: number) => {
    if (!compareData?.document_store_id || togglingId !== null) return;

    setTogglingId(differenceId);
    try {
      const response = await documentCompareService.toggleDislike(
        compareData._id,
        differenceId
      );

      if (response.success) {
        // Update the local state to reflect the change
        setCompareData((prev: any) => {
          const updatedDifferences = prev.result.differences.map((diff: any) =>
            diff.id === differenceId
              ? { ...diff, disliked: !diff.disliked }
              : diff
          );
          return {
            ...prev,
            result: {
              ...prev.result,
              differences: updatedDifferences,
            },
          };
        });
        toast.success(
          response.data?.disliked
            ? "Feedback submitted successfully"
            : "Feedback submitted successfully"
        );
      }
    } catch (error: any) {
      console.error("Error toggling dislike:", error);
      toast.error("Failed to update feedback");
    } finally {
      setTogglingId(null);
    }
  };

  const getDifferenceTypeBadge = (type: string) => {
    switch (type) {
      case "TEXT_OMMISSION":
        return {
          color: "bg-blue-100 text-blue-700",
          text: "TEXT OMMISSION",
        };
      case "CONTEXT_CHANGE":
        return {
          color: "bg-purple-100 text-purple-700",
          text: "CONTEXT CHANGE",
        };
      case "NEW_TEXT":
        return {
          color: "bg-blue-100 text-blue-700",
          text: "NEW TEXT",
        };
      case "SPELLING_ERROR":
        return {
          color: "bg-red-100 text-red-700",
          text: "SPELLING ERROR",
        };
      case "TEXT_SWAPPED":
        return {
          color: "bg-purple-100 text-purple-700",
          text: "TEXT SWAPPED",
        };
      default:
        return {
          color: "bg-gray-100 text-gray-700",
          text: type,
        };
    }
  };

  const getSignificanceBadge = (significance: string) => {
    const sig = significance?.toLowerCase() || "medium";
    return {
      color:
        sig === "high"
          ? "bg-red-100 text-red-700"
          : sig === "low"
          ? "bg-green-100 text-green-700"
          : "bg-yellow-100 text-yellow-700",
      text: significance || "Medium",
    };
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="min-h-[80vh] flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <Loader />
          </div>
        </div>
      </div>
    );
  }

  if (!compareData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="min-h-[80vh] flex items-center justify-center">
          <div className="text-center">
            <h3 className="text-xl font-bold text-gray-900 mb-3">
              Comparison Not Found
            </h3>
            <button
              onClick={() => navigate("/document-compare")}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              Back to Compare
            </button>
          </div>
        </div>
      </div>
    );
  }

  const result = compareData.result || {};
  const differences = result.differences || [];

  // Apply both type filter and disliked filter
  let filteredDifferences = differences;

  // Filter by type
  if (filterType !== "all") {
    filteredDifferences = filteredDifferences.filter(
      (d: any) => d.difference_type === filterType
    );
  }

  // Filter by liked/disliked
  if (filterDisliked === "liked") {
    filteredDifferences = filteredDifferences.filter((d: any) => !d.disliked);
  } else if (filterDisliked === "disliked") {
    filteredDifferences = filteredDifferences.filter((d: any) => d.disliked);
  }

  const totalChanges = differences.length;
  const likeCount = differences.filter((d: any) => !d.disliked).length;
  const dislikeCount = differences.filter((d: any) => d.disliked).length;

  // Get unique difference types from the response dynamically
  const uniqueDifferenceTypes = Array.from(
    new Set(differences.map((d: any) => d.difference_type))
  ).filter(Boolean) as string[];


 const handleDownload = async () => {
    setDownloadLoading(true);

    try {
      const payload = {
        document_compare_file_result_id: compareId,
        document_feedback_value: true,
      };

      const response = await documentCompareService.getDownloadLink(payload);

      if (response.success && response.data?.url) {
        const fileUrl = response.data.url;

        const newTab = window.open();
        if (newTab) {
          newTab.location.href = fileUrl;
        } else {
          toast.error("Please allow popups to download the file");
        }

        toast.success("File download started!");
      } else {
        toast.error("Failed to get file URL");
        console.error("Failed response:", response);
      }
    } catch (error: any) {
      console.error("Error downloading file:", error);
       if (error?.response?.data?.error?.error_description) {
        toast.error(error?.response?.data?.error?.error_description);
      } else if (error?.response?.data?.message) {
        toast.error(error?.response?.data?.message);
      } else {
        toast.error("Something went wrong while downloading the file");
      }
    } finally {
      setDownloadLoading(false);
    }
  };


  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-6 pt-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/document-compare")}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  Modifications Overview - Results ({totalChanges})
                </h1>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-600">Feedback:</span>
                <button
                  onClick={() => setFilterDisliked("all")}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded transition-colors ${
                    filterDisliked === "all"
                      ? "bg-indigo-600 text-white"
                      : "border border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <span className="font-semibold">All ({totalChanges})</span>
                </button>
                <button
                  onClick={() => setFilterDisliked("liked")}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded transition-colors ${
                    filterDisliked === "liked"
                      ? "bg-teal-600 text-white"
                      : "border border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <ThumbsUp className="h-4 w-4" />
                  <span className="font-semibold">{likeCount}</span>
                </button>
                <button
                  onClick={() => setFilterDisliked("disliked")}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded transition-colors ${
                    filterDisliked === "disliked"
                      ? "bg-red-600 text-white"
                      : "border border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <ThumbsDown className="h-4 w-4" />
                  <span className="font-semibold">{dislikeCount}</span>
                </button>
              </div>

              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">Filter by type:</label>
                <div className="relative inline-block w-48">
                  <select
                    className="appearance-none w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg text-sm bg-white text-gray-700 transition-all duration-150 cursor-pointer"
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                  >
                    <option value="all" className="text-gray-600">
                      All Types
                    </option>
                    {uniqueDifferenceTypes.map((type) => {
                      const badge = getDifferenceTypeBadge(type);
                      return (
                        <option key={type} value={type}>
                          {badge.text}
                        </option>
                      );
                    })}
                  </select>

                  {/* Custom dropdown arrow */}
                  <svg
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>

              {/* View Mode Toggle */}
              <div className="flex items-center gap-1 border border-gray-300 rounded-md p-1">
                <button
                  onClick={() => setViewMode("table")}
                  className={`p-2 rounded transition-colors ${
                    viewMode === "table"
                      ? "bg-blue-600 text-white"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                  title="Table View"
                >
                  <LayoutList className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode("cards")}
                  className={`p-2 rounded transition-colors ${
                    viewMode === "cards"
                      ? "bg-blue-600 text-white"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                  title="Card View"
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
              </div>

              <button
                onClick={() => setShowChartModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-800"
                title="View Statistics Chart"
              >
                <PieChart className="h-4 w-4" />
                <span>Chart</span>
              </button>

              <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-800" 
              type="button"
              onClick={handleDownload}
              >
                {downloadLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Downloading...</span>
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4" />
                    <span>Download</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-6">
        {viewMode === "table" ? (
          /* Table View */
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b-2 border-b-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-16">
                      S.NO.
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-40">
                      Discrepancy Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Source Document Reference
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Target Document Detail
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-24">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-64">
                      Location Context
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-24">
                      Feedback
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredDifferences.length > 0 ? (
                    filteredDifferences.map((diff: any, index: number) => {
                      const typeBadge = getDifferenceTypeBadge(
                        diff.difference_type
                      );
                      const significanceBadge = getSignificanceBadge(
                        diff.significance
                      );

                      return (
                        <tr
                          key={diff.id}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-4 py-4 text-sm text-gray-900 font-medium">
                            {index + 1}
                          </td>
                          <td className="px-4 py-4">
                            <span
                              className={`inline-block px-2 py-1 text-xs font-semibold rounded ${typeBadge.color}`}
                            >
                              {typeBadge.text}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-700">
                            <div className="text-teal-700 font-mono text-xs leading-relaxed">
                              {diff.source_document_reference}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-sm">
                            <div className="text-red-700 font-mono text-xs leading-relaxed">
                              {diff.target_document_detail}
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <span
                              className={`inline-block px-3 py-1 text-xs font-semibold rounded ${significanceBadge.color}`}
                            >
                              {significanceBadge.text}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-xs text-gray-700">
                            {diff.location && (
                              <div className="space-y-1">
                                <div>
                                  <span className="font-semibold">
                                    Page no (Target):
                                  </span>{" "}
                                  {diff.location.page_no_target || "N/A"}
                                  {diff.location.para_no_target && (
                                    <>
                                      ,{" "}
                                      <span className="font-semibold">
                                        Para no (Target):
                                      </span>{" "}
                                      {diff.location.para_no_target}
                                    </>
                                  )}
                                  {diff.location.line_no_target && (
                                    <>
                                      ,{" "}
                                      <span className="font-semibold">
                                        Line no (Target):
                                      </span>{" "}
                                      {diff.location.line_no_target}
                                    </>
                                  )}
                                </div>
                                {diff.location.location_context_target && (
                                  <div className="mt-2 p-2 bg-gray-50 rounded text-xs">
                                    <div className="font-semibold text-gray-700 mb-1">
                                      Context:
                                    </div>
                                    <div className="text-gray-600 font-mono leading-relaxed">
                                      {diff.location.location_context_target}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            <button
                              onClick={() => handleToggleDislike(diff.id)}
                              disabled={togglingId === diff.id}
                              className={`p-2 rounded transition-colors ${
                                togglingId === diff.id
                                  ? "opacity-50 cursor-not-allowed"
                                  : diff.disliked
                                  ? "bg-red-100 hover:bg-red-200"
                                  : "hover:bg-gray-100"
                              }`}
                              title={
                                togglingId === diff.id
                                  ? "Processing..."
                                  : diff.disliked
                                  ? "Remove dislike"
                                  : "Mark as disliked"
                              }
                            >
                              {togglingId === diff.id ? (
                                <Loader2 className="h-5 w-5 text-indigo-600 animate-spin" />
                              ) : (
                                <ThumbsDown
                                  className={`h-5 w-5 ${
                                    diff.disliked
                                      ? "text-red-600 fill-red-600"
                                      : "text-gray-400 hover:text-red-500"
                                  }`}
                                />
                              )}
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={7} className="px-4 py-12 text-center">
                        <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-3" />
                        <h3 className="text-base font-bold text-gray-900 mb-1">
                          No Differences Found
                        </h3>
                        <p className="text-sm text-gray-500">
                          {filterType === "all" && filterDisliked === "all"
                            ? "The documents are identical"
                            : "No differences match the selected filters"}
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          /* Card View */
          <div className="space-y-4">
            {filteredDifferences.length > 0 ? (
              filteredDifferences.map((diff: any, index: number) => {
                const typeBadge = getDifferenceTypeBadge(diff.difference_type);
                const significanceBadge = getSignificanceBadge(
                  diff.significance
                );

                return (
                  <div
                    key={diff.id}
                    className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200"
                  >
                    {/* Header */}
                    <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gray-50">
                      <div className="flex items-center gap-4">
                        <div className="w-8 h-8 flex items-center justify-center rounded-md bg-gray-200 text-gray-700 text-sm font-medium">
                          {index + 1}
                        </div>
                        <div className="flex items-center gap-2">
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-md ${typeBadge.color}`}
                          >
                            {typeBadge.text}
                          </span>
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-md ${significanceBadge.color}`}
                          >
                            {significanceBadge.text}
                          </span>
                        </div>
                      </div>

                      <button
                        onClick={() => handleToggleDislike(diff.id)}
                        disabled={togglingId === diff.id}
                        className={`p-2 rounded-md transition-all duration-200 ${
                          togglingId === diff.id
                            ? "opacity-50 cursor-not-allowed"
                            : diff.disliked
                            ? "bg-red-50 hover:bg-red-100"
                            : "hover:bg-gray-100"
                        }`}
                        title={
                          togglingId === diff.id
                            ? "Processing..."
                            : diff.disliked
                            ? "Remove dislike"
                            : "Mark as disliked"
                        }
                      >
                        {togglingId === diff.id ? (
                          <Loader2 className="h-5 w-5 text-gray-600 animate-spin" />
                        ) : (
                          <ThumbsDown
                            className={`h-5 w-5 ${
                              diff.disliked
                                ? "text-red-500 fill-red-500"
                                : "text-gray-400"
                            }`}
                          />
                        )}
                      </button>
                    </div>

                    {/* Body */}
                    <div className="p-5 space-y-5">
                      {/* Source & Target */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                          <div className="text-xs font-semibold text-gray-600 uppercase mb-1">
                            Source Document
                          </div>
                          <div className="text-sm font-mono leading-relaxed text-blue-700">
                            {diff.source_document_reference}
                          </div>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                          <div className="text-xs font-semibold text-gray-600 uppercase mb-1">
                            Target Document
                          </div>
                          <div className="text-sm font-mono leading-relaxed text-green-700">
                            {diff.target_document_detail}
                          </div>
                        </div>
                      </div>

                      {/* Location */}
                      {diff.location && (
                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                          <div className="text-xs font-semibold text-gray-600 uppercase mb-3 flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-gray-500" />
                            Location Details
                          </div>
                          <div className="grid grid-cols-3 gap-3 mb-3">
                            <div className="bg-white border border-gray-100 rounded-md p-2">
                              <div className="text-xs text-gray-500 mb-1">
                                Page
                              </div>
                              <div className="text-sm font-medium text-gray-800">
                                {diff.location.page_no_target || "N/A"}
                              </div>
                            </div>
                            <div className="bg-white border border-gray-100 rounded-md p-2">
                              <div className="text-xs text-gray-500 mb-1">
                                Paragraph
                              </div>
                              <div className="text-sm font-medium text-gray-800">
                                {diff.location.para_no_target || "N/A"}
                              </div>
                            </div>
                            <div className="bg-white border border-gray-100 rounded-md p-2">
                              <div className="text-xs text-gray-500 mb-1">
                                Line
                              </div>
                              <div className="text-sm font-medium text-gray-800">
                                {diff.location.line_no_target || "N/A"}
                              </div>
                            </div>
                          </div>
                          {diff.location.location_context_target && (
                            <div className="bg-white border border-gray-100 rounded-md p-3">
                              <div className="text-xs font-semibold text-gray-500 mb-1">
                                Context
                              </div>
                              <div className="text-xs text-gray-700 font-mono leading-relaxed">
                                {diff.location.location_context_target}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                <CheckCircle2 className="h-14 w-14 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-800 mb-1">
                  No Differences Found
                </h3>
                <p className="text-sm text-gray-500">
                  {filterType === "all" && filterDisliked === "all"
                    ? "The documents are identical."
                    : "No differences match the selected filters."}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Chart Modal */}
      <ComparisonChartModal
        isOpen={showChartModal}
        onClose={() => setShowChartModal(false)}
        data={compareData}
      />
    </div>
  );
};

export default DocumentCompareResult;
